import classnames from 'classnames';

import { useMoneyFormat } from '@enterprise/hooks';

import type { NegativeFormatProps } from '#formats/types/negative-format.props';

/**
 * Formats and displays a monetary value, applying special styling for negative values.
 *
 * @remarks
 * This component uses the `useMoneyFormat` hook to format the provided value as currency.
 * If the value is negative, an optional `negativeClassName` is applied for custom styling.
 * If the value is `null` or `undefined`, a fallback string is displayed.
 *
 * @param props - The properties for the MoneyFormat component.
 * @param props.className - Additional class name(s) for the root element.
 * @param props.decimals - Number of decimal places to display.
 * @param props.fallback - Fallback string to display if value is `null` or `undefined`. Defaults to `'?'`.
 * @param props.negativeClassName - Class name to apply when the value is negative.
 * @param props.value - The monetary value to format and display.
 * @returns The formatted monetary value wrapped in a `<span>` element.
 */
export const MoneyFormat = ({
  className,
  decimals,
  fallback = '?',
  negativeClassName,
  value,
  ...props
}: NegativeFormatProps) => {
  const isNegative = (value ?? 0) < 0;

  return (
    <span
      {...props}
      className={classnames(className, { [negativeClassName]: isNegative })}>
      {useMoneyFormat(value, decimals, fallback)}
    </span>
  );
};
